import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, IMAGES } from "@/data/site";
import { useLead } from "@/App";
import { EASE } from "./motion";

const scrollTo = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -70, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openConsult } = useLead();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollTo(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 2.6 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "glass border-b hairline" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-6 md:px-12">
          <a
            href="#home"
            onClick={(e) => go(e, "#home")}
            data-testid="nav-logo"
            className="group flex items-center gap-3.5"
          >
            <span className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_4px_20px_rgba(182,141,64,0.28)] ring-1 ring-[#B68D40]/60 transition-transform duration-500 group-hover:scale-105">
              <img src={IMAGES.logo} alt="Bengaluru Interior Design Company logo" className="h-10 w-10 object-contain" />
            </span>
            <span className="flex flex-col justify-center">
              <span className={`font-display text-xl md:text-[22px] leading-none tracking-tight transition-colors duration-500 ${scrolled ? "text-[#1B1B1B]" : "text-[#F7F5F2]"}`}>
                Bengaluru <span className="italic font-medium text-[#B68D40]">Interiors</span>
              </span>
              <span className={`mt-1.5 flex items-center gap-1.5 font-num text-[8.5px] tracking-[0.4em] uppercase transition-colors duration-500 ${scrolled ? "text-[#2C2C2C]/55" : "text-[#F7F5F2]/65"}`}>
                By
                <span className="h-[3px] w-[3px] rotate-45 bg-[#B68D40]" />
                Sohan
                <span className="h-[3px] w-[3px] rotate-45 bg-[#B68D40]" />
                Raju
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`group relative text-[13px] font-medium tracking-wide transition-colors duration-500 ${
                  scrolled ? "text-[#2C2C2C]" : "text-[#F7F5F2]/85"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#B68D40] transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
            <button
              onClick={() => openConsult("navbar")}
              data-testid="nav-book-consultation-btn"
              className="rounded-full bg-[#1B1B1B] px-6 py-2.5 text-[13px] font-semibold text-[#F7F5F2] transition-colors duration-300 hover:bg-[#B68D40]"
            >
              Book Consultation
            </button>
          </nav>

          <button
            className={`lg:hidden ${scrolled ? "text-[#1B1B1B]" : "text-[#F7F5F2]"}`}
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-[#1B1B1B] px-8 py-8"
            initial={{ opacity: 0, y: "-4%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-4%" }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white">
                  <img src={IMAGES.logo} alt="Bengaluru Interiors logo" className="h-8 w-8 object-contain" />
                </span>
                <span className="font-display text-xl text-[#F7F5F2]">
                  Bengaluru <span className="italic text-[#B68D40]">Interiors</span>
                </span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close-btn" className="text-[#F7F5F2]" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="border-b border-[#F7F5F2]/10 py-4 font-display text-3xl text-[#F7F5F2]"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: EASE }}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.button
              onClick={() => {
                setOpen(false);
                openConsult("mobile_menu");
              }}
              data-testid="mobile-book-consultation-btn"
              className="mt-10 rounded-full bg-[#B68D40] py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
            >
              Book Free Consultation
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
